import { FC } from 'react';
import { Heart, MessageCircle, TrendingUp, ExternalLink } from 'lucide-react';
import { LastScrapedPost } from '../types';

interface PostMetricsFooterProps {
  post: LastScrapedPost;
  companyName: string;
  isFirst?: boolean;
  isLast?: boolean;
  totalCount?: number;
}

/**
 * Creative footer component displaying last scraped post metrics
 * Shows engagement metrics in an attractive, easy-to-read format
 */
export const PostMetricsFooter: FC<PostMetricsFooterProps> = ({ 
  post, 
  isLast = true
}) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHours === 0) {
          const diffMins = Math.floor(diffMs / (1000 * 60));
          return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
        }
        return `${diffHours}h ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        });
      }
    } catch {
      return dateString;
    }
  };


  return (
    <div className={`px-3 py-2 ${!isLast ? 'border-b border-gray-100' : ''} hover:bg-gray-50/30 transition-colors`}>
      <div className="flex items-center gap-4">
        {/* Left: Title - Fixed Width */}
        <div className="flex-shrink-0" style={{ width: '400px' }}>
          {post.title ? (
            <span className="text-xs text-gray-700 truncate font-medium block">
              {post.title}
            </span>
          ) : (
            <span className="text-xs text-gray-500 truncate block">
              Post
            </span>
          )}
        </div>

        {/* Center: Date - Evenly Spaced */}
        {post.scrapedAt && (
          <div className="flex items-center gap-1.5 flex-1">
            <span className="text-xs text-gray-300">•</span>
            <span className="text-xs text-gray-500">
              Monitored {formatDate(post.scrapedAt)}
            </span>
          </div>
        )}

        {/* Center: Metrics - Evenly Spaced */}
        <div className="flex items-center gap-4 flex-1 justify-center">
          <div className="flex items-center gap-1.5">
            <Heart className="w-3 h-3 text-rose-400" />
            <span className="text-xs font-semibold text-gray-700">{formatNumber(post.likes)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MessageCircle className="w-3 h-3 text-slate-400" />
            <span className="text-xs font-semibold text-gray-700">{formatNumber(post.comments)}</span>
          </div>
          {post.engagementScore !== undefined && (
            <div className="flex items-center gap-1.5 ml-2 pl-3 border-l border-gray-200">
              <TrendingUp className="w-3 h-3 text-amber-500" />
              <span className="text-xs font-semibold text-gray-700">
                {post.engagementScore.toFixed(0)}
              </span>
            </div>
          )}
        </div>

        {/* Right: External Link - Evenly Spaced */}
        <div className="flex-1 flex justify-end">
          {post.url && (
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
              title="View post"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

